import {Injectable} from "@angular/core";

import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {AutoRetryRequest, BaseService} from "app/services/domain/base.service";
import {BillingPlanDefinitionVersionsService} from "app/services/domain/billing/billing-plan/billing-plan-versions.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {ControlOption} from "framework/ui/control-option";
import {mapInParallelNoNulls} from "framework/utils/concurrency";

@Injectable()
export class BillingPlanDefinitionsService extends SharedSvc.BaseDefinitionService<Models.BillingPlanConfiguration, Models.BillingPlanDefinition, Models.BillingPlanDefinitionVersion, BillingPlanDefinitionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.BillingPlanDefinition, BillingPlanDefinitionExtended.newInstance);
    }

    createDefinition(): Promise<Models.BillingPlanDefinition>
    {
        return this.api.billingPlanDefinitionsApi.create();
    }

    setState(sysId: string,
             model: Models.BillingPlanDefinition): Promise<Models.BillingPlanDefinition>
    {
        return this.api.billingPlanDefinitionsApi.setState(sysId, model);
    }

    async remove(sysId: string): Promise<boolean>
    {
        await this.api.billingPlanDefinitionsApi.remove(sysId);

        return true;
    }

    @ReportError
    public async getAll(req?: Models.BillingPlanDefinitionListRequest): Promise<Models.RecordIdentity[]>
    {
        if (!req)
        {
            req = Models.BillingPlanDefinitionListRequest.newInstance({
                                                                          filters: Models.BillingPlanDefinitionFilters.newInstance({})
                                                                      });
        }

        const res = await this.api.billingPlanDefinitionsApi.list(req);
        return res?.results.map((id) => Models.RecordIdentity.newInstance({
                                                                              sysId: id
                                                                          }));
    }

    public async getAllOptions(): Promise<ControlOption<string>[]>
    {
        const result: ControlOption<string>[] = [];
        const extendedList = await this.getExtendedAll();

        for (const ext of extendedList) 
        {
            if (ext?.model?.is_active) result.push(new ControlOption(ext.model.sysId, ext.model.name));
        }

        return result;
    }

    public async getHistory(sysId: string): Promise<Models.BillingPlanDefinitionVersion[]>
    {
        const ids = await this.api.billingPlanDefinitionsApi.getHistory(sysId);

        return mapInParallelNoNulls(ids, async (id) =>
        {
            return this.domain.billingPlanDefinitionVersions.get(id, null);
        });
    }

    protected cachePrefix(): string { return "BillingPlanDefinition"; }

    public getCacheKey(id: string): string
    {
        return BaseService.generateCacheKey(this.cachePrefix(), id);
    }

    protected getRaw(id: string): Promise<Models.BillingPlanDefinition>
    {
        return AutoRetryRequest(20, () => this.api.billingPlanDefinitionsApi.get(id));
    }

    protected getBatchRaw(ids: string[]): Promise<Models.BillingPlanDefinition[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => BillingPlanDefinitionExtended.newIdentity(id))});
        return this.api.billingPlanDefinitionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }
}

export class BillingPlanDefinitionExtended extends SharedSvc.ExtendedDefinitionModel<Models.BillingPlanConfiguration, Models.BillingPlanDefinition, Models.BillingPlanDefinitionVersion>
{
    static newInstance(svc: BillingPlanDefinitionsService,
                       model: Models.BillingPlanDefinition): BillingPlanDefinitionExtended
    {
        return new BillingPlanDefinitionExtended(svc, model, "BillingPlanDefinition");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "BillingPlanDefinition",
                                                     sysId: sysId
                                                 });
    }

    get isOwned(): boolean
    {
        const userDetails = this.domain.users.userDetails;
        return this.model.owner === userDetails.user.id;
    }

    async setState(model: Models.BillingPlanDefinition): Promise<void>
    {
        if (this.model.sysId)
        {
            let svc = this.domain.billingPlanDefinitions;
            svc.errors.dismiss();

            await svc.flush(this);
            this.model.name        = model.name;
            this.model.description = model.description;
            this.model.is_active   = model.is_active || false;

            this.model = await svc.api.billingPlanDefinitionsApi.setState(this.model.sysId, this.model);
        }
    }

    protected definitionSvc(): BillingPlanDefinitionsService
    {
        return this.domain.billingPlanDefinitions;
    }

    protected definitionVersionSvc(): BillingPlanDefinitionVersionsService
    {
        return this.domain.billingPlanDefinitionVersions;
    }
}


